/**
 * App-specific FAQ content. NutriScan and CardCycle entries are owned by the
 * app pages and their legal review (tasks T04-T06) and are unchanged here.
 *
 * The former studio-wide `faqs` list was removed with the global /faq route in
 * task T03: it carried stale platform, pricing, and security-mailbox claims.
 */
export const nsfaqs = [
  {
    q: 'How does NutriScan work?',
    a: 'Open the app, point your camera at any barcode or nutrition label, and NutriScan extracts the ingredient list using on-device OCR. The extracted text is then sent to our AI analysis engine, which classifies each ingredient and generates a health score.',
  },
  {
    q: 'Is NutriScan free?',
    a: 'NutriScan includes a free allowance of 3 scans in a rolling 24-hour period. An optional subscription is available through Google Play.',
  },
  {
    q: 'Does NutriScan work offline?',
    a: 'The camera and label detection work offline. However, AI-powered ingredient analysis requires an internet connection because the analysis is processed server-side to ensure accuracy and up-to-date ingredient data.',
  },
  {
    q: 'How accurate is the ingredient analysis?',
    a: 'Our analysis engine is powered by Google\'s Gemini AI and draws from a database of over 12,000 classified food additives. While we strive for accuracy, NutriScan is for educational purposes and should not replace advice from a registered dietitian or doctor.',
  },
  {
    q: 'What does the health score mean?',
    a: 'The health score (0–100) reflects the overall nutritional profile of a product based on its ingredient list, serving size, and your dietary preferences. 80+ is Excellent, 60–79 is Good, 40–59 is Moderate, and below 40 is Poor.',
  },
  {
    q: 'Does NutriScan diagnose allergies?',
    a: 'No. NutriScan highlights ingredients that commonly cause allergic reactions, but it is not a medical diagnostic tool. Always read product labels yourself and consult an allergist for dietary management of allergies.',
  },
  {
    q: 'What data does NutriScan collect?',
    a: 'NutriScan handles account and profile information, the label information you submit for a scan, diagnostics, subscription records where you have one, and the details you include in a support request. The NutriScan Privacy Policy describes each category, its purpose, and the services involved.',
  },
  {
    q: 'Can I delete my data?',
    a: 'Yes. Request deletion in the app while signed in, or email support from the Google address you use for NutriScan. A verified request starts a 30-day recovery period, after which the account and associated personal information are deleted or de-identified, apart from limited records we must retain. Account deletion does not cancel a Google Play subscription. See the NutriScan Account Deletion page.',
  },
  {
    q: 'Is NutriScan available on Android?',
    a: 'Yes. NutriScan is available on Google Play.',
  },
  {
    q: 'The scan result looks wrong. What should I do?',
    a: 'AI analysis can occasionally misread labels in poor lighting or from heavily formatted packages. Try rescanning in better light or use the manual entry option. You can also report incorrect results using the flag icon on any scan result screen.',
  },
];

// CardCycle 0.3.0 FAQ. Content is the approved public FAQ from
// RC_PUBLIC_PRIVACY_TERMS_FAQ_0.3.0 (no-SMS release) — do not reword the
// substance here without updating that source. `link` is optional and is
// rendered as a trailing anchor by CCFaqItem.
export const ccfaqs = [
  {
    q: 'Does CardCycle need an account?',
    a: 'No. CardCycle is designed to work without sign-in or a CardCycle server account.',
  },
  {
    q: 'Does CardCycle connect to my bank or move money?',
    a: 'No. It is not a bank or payment processor and cannot make or guarantee a payment.',
  },
  {
    q: 'Why does CardCycle ask for only the last four card digits?',
    a: 'They help you recognise a card without entering a full card number. CardCycle must never request a full card number, CVV, PIN, OTP, or bank login.',
  },
  {
    q: 'What can CardCycle access on my phone?',
    a: 'Notifications, if you allow reminders. Biometrics or your device PIN, if you turn on app lock. A file you pick yourself, when you import or export. Nothing else — no messages, call logs, contacts, location, camera, microphone, or bank accounts.',
  },
  {
    q: 'Does CardCycle read my bank messages?',
    a: 'No. This version does not access SMS at all, does not monitor messages, and does not use a notification listener. Every record is one you entered or confirmed yourself.',
  },
  {
    q: 'What does CardCycle store on my device?',
    a: 'Depending on the features you use: card nicknames, issuers, masked last four digits, statement and due dates, reminders, notification history, settings, app-lock settings, cycle actions, statement records, transactions, payment evidence, and files you choose to import. All of it stays on your device unless you export a file yourself.',
  },
  {
    q: 'Does CardCycle send my card data to Tickle Apps?',
    a: 'No. This build ships without the Android internet permission, so it cannot make a network request at all.',
  },
  {
    q: 'Are backups encrypted?',
    a: 'Not by CardCycle. An exported file is your responsibility and should be stored only where you trust the destination.',
  },
  {
    q: 'Can I delete my data?',
    a: 'Yes. You can delete records, clear app data, or uninstall the app. Local-only data may not be recoverable afterward.',
  },
  {
    q: 'Why did a reminder or estimate differ from my issuer?',
    a: 'CardCycle uses the information you entered or confirmed. Always verify final dates, amounts, payments, and issuer changes with your bank or card issuer.',
  },
  {
    q: 'Where can I get CardCycle?',
    a: 'CardCycle is published for Android on Google Play.',
    link: { href: 'https://play.google.com/store/apps/details?id=com.tickleapps.cardcycle', label: 'View CardCycle on Google Play' },
  },
];
